import { useEffect, useState } from "react";
import { api } from "@/lib/api";

const cache = new Map();

async function shrink(file, max = 800, keepAlpha = false) {
  if (!file.type.startsWith("image/") || !window.createImageBitmap) return file;
  const bmp = await createImageBitmap(file);
  const s = Math.min(1, max / Math.max(bmp.width, bmp.height));
  const c = document.createElement("canvas");
  c.width = Math.round(bmp.width * s);
  c.height = Math.round(bmp.height * s);
  c.getContext("2d").drawImage(bmp, 0, 0, c.width, c.height);
  if (keepAlpha) {
    const blob = await new Promise((r) => c.toBlob(r, "image/png"));
    return blob ? new File([blob], file.name.replace(/\.\w+$/, "") + ".png", { type: "image/png" }) : file;
  }
  const blob = await new Promise((r) => c.toBlob(r, "image/jpeg", 0.82));
  return blob ? new File([blob], file.name.replace(/\.\w+$/, "") + ".jpg", { type: "image/jpeg" }) : file;
}

export async function uploadFile(file, kind) {
  const fd = new FormData();
  fd.append("file", await shrink(file, kind === "clinic" ? 1600 : 800, ["signature", "stamp", "logo"].includes(kind)));
  const { data } = await api.post(`/files?kind=${kind}`, fd, { timeout: 60000 });
  return data;
}

export async function fileUrl(id) {
  if (cache.has(id)) return cache.get(id).url;
  const { data } = await api.get(`/files/${id}`, { responseType: "blob" });
  const url = URL.createObjectURL(data);
  cache.set(id, { url, type: data.type || "" });
  return url;
}

export function useFileMeta(id) {
  const [meta, setMeta] = useState(() => (id ? cache.get(id) || null : null));
  useEffect(() => {
    if (!id) { setMeta(null); return undefined; }
    let alive = true;
    fileUrl(id).then(() => alive && setMeta(cache.get(id))).catch(() => alive && setMeta(null));
    return () => { alive = false; };
  }, [id]);
  return meta;
}

export function useFileUrl(id) {
  return useFileMeta(id)?.url || null;
}
